import React, { useEffect, useState, useMemo } from "react";
import { Box, Typography, Card, Chip, CircularProgress } from "@mui/material";
import EventNoteIcon from "@mui/icons-material/EventNote";
import ChatBubbleIcon from "@mui/icons-material/ChatBubble";
import EmailIcon from "@mui/icons-material/Email";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import SyncAltIcon from "@mui/icons-material/SyncAlt";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import TimerIcon from "@mui/icons-material/Timer";
import { motion } from "framer-motion";
import api from "../../utils/api";

/* ── Status chip colours ─────────────────────────────────────────────────── */
const STATUS_COLORS = {
  open:        { bg: "rgba(234,88,12,0.12)",   text: "#ea580c" },
  in_progress: { bg: "rgba(37,99,235,0.12)",   text: "#2563eb" },
  pending:     { bg: "rgba(217,119,6,0.12)",   text: "#b45309" },
  approved:    { bg: "rgba(22,163,74,0.12)",   text: "#16a34a" },
  denied:      { bg: "rgba(220,38,38,0.12)",   text: "#dc2626" },
  resolved:    { bg: "rgba(22,163,74,0.12)",   text: "#16a34a" },
  closed:      { bg: "rgba(107,114,128,0.12)", text: "#6b7280" },
  cancelled:   { bg: "rgba(220,38,38,0.12)",   text: "#dc2626" },
  submitted:   { bg: "rgba(139,92,246,0.12)",  text: "#7c3aed" },
  under_review:{ bg: "rgba(37,99,235,0.12)",   text: "#2563eb" },
};

const statusChipSx = (status = "") => {
  const key   = status.toLowerCase().replace(/\s+/g, "_");
  const style = STATUS_COLORS[key] || { bg: "rgba(47,99,173,0.1)", text: "#2f63ad" };
  return {
    height: 20,
    borderRadius: "6px",
    fontWeight: 700,
    fontSize: "0.67rem",
    backgroundColor: style.bg,
    color: style.text,
    "& .MuiChip-label": { px: "6px" },
  };
};

/* ── Timeline type config ────────────────────────────────────────────────── */
const timelineTypeConfig = {
  architecturalRequest: {
    color: "#1E88E5",
    icon:  AssignmentTurnedInIcon,
    label: "Request Created/Updated",
  },
  status: {
    color: "#3949AB",
    icon:  SyncAltIcon,
    label: "Status Change",
  },
  dates: {
    color: "#00ACC1",
    icon:  CalendarMonthIcon,
    label: "Date Update",
  },
  completion_days: {
    color: "#1565C0",
    icon:  TimerIcon,
    label: "Completion Days",
  },
  comment: {
    color: "#43A047",
    icon:  ChatBubbleIcon,
    label: "Comment",
  },
  event: {
    color: "#8E24AA",
    icon:  EventNoteIcon,
    label: "Event",
  },
  email: {
    color: "#FB8C00",
    icon:  EmailIcon,
    label: "Email",
  },
};

const ArchitecturalRequestTimeline = ({ architectural_request_id }) => {
  const [timelineData, setTimelineData] = useState(null);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState(null);

  const pageCardSx = {
    borderRadius: "18px",
    border: "1px solid rgba(47,99,173,0.08)",
    boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
    backgroundColor: "rgba(255,255,255,0.96)",
  };

  useEffect(() => {
    const fetchTimeline = async () => {
      try {
        setLoading(true);
        const response = await api.get(`/architecturalRequestTimeline/${architectural_request_id}`);
        setTimelineData(response.data);
      } catch (err) {
        console.error("Error fetching timeline data:", err);
        setError("Failed to load timeline data.");
      } finally {
        setLoading(false);
      }
    };
    if (architectural_request_id) fetchTimeline();
  }, [architectural_request_id]);

  const combinedTimeline = useMemo(() => {
    if (!timelineData?.architecturalRequest) return [];

    const {
      architecturalRequest,
      comments              = [],
      events                = [],
      emails                = [],
      statusHistory         = [],
      dateHistory           = [],
      completionDaysHistory = [],
    } = timelineData;

    const timeline = [
      {
        type:  "architecturalRequest",
        title: "Architectural Request Submitted",
        time:  architecturalRequest.created_at,
        by:    architecturalRequest.created_by_name || "System",
        email: architecturalRequest.created_by_email || "",
      },

      ...statusHistory.map(({ old_status, new_status, changed_at, changed_by_name, changed_by_email, comment_text }) => ({
        type:       "status",
        title:      `Status changed from "${old_status || "N/A"}" → "${new_status || "N/A"}"`,
        time:       changed_at || new Date().toISOString(),
        by:         changed_by_name  || "System",
        email:      changed_by_email || "",
        comment:    comment_text     || "",
        old_status,
        new_status,
      })),

      ...dateHistory.map(({ old_start, new_start, old_end, new_end, changed_at, changed_by_name, changed_by_email }) => ({
        type:  "dates",
        title: "Anticipated Dates Updated",
        time:  changed_at || new Date().toISOString(),
        by:    changed_by_name  || "System",
        email: changed_by_email || "",
        details: {
          old_start,
          new_start,
          old_end,
          new_end,
          changed_start:
            old_start && new_start &&
            new Date(old_start).toDateString() !== new Date(new_start).toDateString(),
          changed_end:
            old_end && new_end &&
            new Date(old_end).toDateString() !== new Date(new_end).toDateString(),
        },
      })),

      ...completionDaysHistory.map((row) => ({
        type:             "completion_days",
        title:            row.restart_number <= 1 ? "Completion Days Set" : `Completion Days Updated (Restart #${row.restart_number})`,
        time:             row.recorded_at || new Date().toISOString(),
        by:               row.recorded_by_name  || "System",
        email:            row.recorded_by_email || "",
        days_to_complete: row.days_to_complete,
        notes:            row.notes || "",
        restart_number:   row.restart_number,
      })),

      ...comments.map(({ comment_text, sent_at, user_name, user_email }) => ({
        type:  "comment",
        title: comment_text,
        time:  sent_at || new Date().toISOString(),
        by:    user_name  || "Unknown User",
        email: user_email || "",
      })),

      ...events.map(({ title, created_at }) => ({
        type:  "event",
        title,
        time:  created_at || new Date().toISOString(),
      })),

      ...emails.map(({ subject, sent_at, email_address }) => ({
        type:  "email",
        title: subject,
        time:  sent_at || new Date().toISOString(),
        by:    email_address || "System Notification",
      })),

      ...(architecturalRequest.updated_at
        ? [
            {
              type:  "architecturalRequest",
              title: "Architectural Request Updated",
              time:  architecturalRequest.updated_at,
              by:    architecturalRequest.reviewed_by_name || architecturalRequest.created_by_name || "System",
            },
          ]
        : []),
    ];

    return timeline.sort((a, b) => new Date(a.time) - new Date(b.time));
  }, [timelineData]);

  if (loading) {
    return (
      <Box sx={{ minHeight: "180px", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Typography color="error" sx={{ textAlign: "center" }}>{error}</Typography>;
  }

  if (combinedTimeline.length === 0) {
    return (
      <Card sx={{ ...pageCardSx, p: 2.5, textAlign: "center" }}>
        <Typography variant="body1" sx={{ fontWeight: 600, color: "#223142" }}>
          No timeline activity found
        </Typography>
        <Typography variant="body2" sx={{ color: "#6b778c", mt: 0.5 }}>
          Timeline activity will appear here as the request is updated.
        </Typography>
      </Card>
    );
  }

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 700, color: "#223142" }}>
        Architectural Request Timeline
      </Typography>

      <Box
        sx={{
          position: "relative",
          pl: { xs: 2, sm: 2.5 },
          "&::before": {
            content: '""',
            position: "absolute",
            left: { xs: "9px", sm: "11px" },
            top: 8,
            bottom: 8,
            width: "2px",
            backgroundColor: "rgba(47,99,173,0.12)",
          },
        }}
      >
        {combinedTimeline.map(({ type, title, time, by, email, comment, details, days_to_complete, notes, old_status, new_status }, index) => {
          const config        = timelineTypeConfig[type] || timelineTypeConfig.architecturalRequest;
          const IconComponent = config.icon;

          return (
            <motion.div
              key={`${type}-${time}-${index}`}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.28, delay: index * 0.03 }}
            >
              <Box sx={{ position: "relative", mb: 1.25 }}>
                {/* Timeline dot */}
                <Box
                  sx={{
                    position: "absolute",
                    left: { xs: "-15px", sm: "-18px" },
                    top: 18,
                    width: { xs: 14, sm: 16 },
                    height: { xs: 14, sm: 16 },
                    borderRadius: "999px",
                    backgroundColor: config.color,
                    border: "3px solid #fff",
                    boxShadow: "0 0 0 1px rgba(47,99,173,0.08)",
                    zIndex: 2,
                  }}
                />

                <Card
                  sx={{
                    borderRadius: "14px",
                    border: type === "status"
                      ? `1px solid ${config.color}30`
                      : "1px solid rgba(47,99,173,0.08)",
                    boxShadow: "0 3px 10px rgba(0,0,0,0.04)",
                    backgroundColor: type === "status" ? `${config.color}06` : "#fff",
                    overflow: "hidden",
                  }}
                >
                  <Box sx={{ p: { xs: 1.1, sm: 1.25 } }}>
                    {/* ── Header: icon + title + type chip ── */}
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: { xs: "column", sm: "row" },
                        alignItems: { xs: "stretch", sm: "flex-start" },
                        justifyContent: "space-between",
                        gap: 1,
                        mb: 0.75,
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.9, minWidth: 0, flex: 1 }}>
                        <Box
                          sx={{
                            width: { xs: 30, sm: 32 },
                            height: { xs: 30, sm: 32 },
                            borderRadius: "9px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            backgroundColor: `${config.color}15`,
                            flexShrink: 0,
                          }}
                        >
                          <IconComponent sx={{ color: config.color, fontSize: 16 }} />
                        </Box>

                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 700, color: "#223142", lineHeight: 1.25, wordBreak: "break-word", overflowWrap: "anywhere" }}
                          >
                            {type === "status"
                              ? "Status Change"
                              : type === "dates"
                              ? "Anticipated Dates Updated"
                              : type === "completion_days"
                              ? "Completion Days Updated"
                              : title}
                          </Typography>
                        </Box>
                      </Box>

                      <Box sx={{ display: "flex", justifyContent: { xs: "flex-start", sm: "flex-end" }, width: { xs: "100%", sm: "auto" }, pl: { xs: "38px", sm: 0 } }}>
                        <Chip
                          size="small"
                          label={config.label}
                          sx={{
                            maxWidth: "100%",
                            height: 24,
                            borderRadius: "999px",
                            fontWeight: 700,
                            fontSize: "0.68rem",
                            color: config.color,
                            backgroundColor: `${config.color}15`,
                            "& .MuiChip-label": { display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", px: 1 },
                          }}
                        />
                      </Box>
                    </Box>

                    {/* ── Status change: colored chips + arrow ── */}
                    {type === "status" && (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.75, pl: "38px", flexWrap: "wrap" }}>
                        {old_status ? (
                          <Chip label={old_status} size="small" sx={statusChipSx(old_status)} />
                        ) : (
                          <Typography variant="caption" sx={{ color: "#9aa5b4", fontStyle: "italic" }}>Initial</Typography>
                        )}
                        <SwapHorizIcon sx={{ fontSize: 15, color: "#9aa5b4", flexShrink: 0 }} />
                        <Chip label={new_status || "N/A"} size="small" sx={statusChipSx(new_status)} />
                      </Box>
                    )}

                    {/* ── Status change optional comment ── */}
                    {type === "status" && comment && (
                      <Box sx={{ p: 1, borderRadius: "10px", backgroundColor: "#f9fafb", border: "1px solid rgba(47,99,173,0.06)", mb: 0.75, ml: "38px" }}>
                        <Typography variant="body2" sx={{ color: "#555", fontStyle: "italic", fontSize: "0.82rem", wordBreak: "break-word", overflowWrap: "anywhere" }}>
                          "{comment}"
                        </Typography>
                      </Box>
                    )}

                    {/* ── Date changes ── */}
                    {type === "dates" && details && (
                      <Box sx={{ p: 1, borderRadius: "10px", backgroundColor: "#ecfeff", border: "1px solid rgba(0,172,193,0.12)", mb: 0.75 }}>
                        {details.changed_start && details.old_start && details.new_start && (
                          <Typography variant="body2" sx={{ color: "#006064", mb: 0.35, fontSize: "0.82rem", wordBreak: "break-word" }}>
                            Start: <strong>{new Date(details.old_start).toLocaleDateString()}</strong>{" "}
                            → <strong>{new Date(details.new_start).toLocaleDateString()}</strong>
                          </Typography>
                        )}
                        {details.changed_end && details.old_end && details.new_end && (
                          <Typography variant="body2" sx={{ color: "#006064", fontSize: "0.82rem", wordBreak: "break-word" }}>
                            Completion: <strong>{new Date(details.old_end).toLocaleDateString()}</strong>{" "}
                            → <strong>{new Date(details.new_end).toLocaleDateString()}</strong>
                          </Typography>
                        )}
                      </Box>
                    )}

                    {/* ── Completion days ── */}
                    {type === "completion_days" && (
                      <Box sx={{ p: 1, borderRadius: "10px", backgroundColor: "#E3F2FD", border: "1px solid rgba(21,101,192,0.14)", mb: 0.75 }}>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: "#0D47A1", fontSize: "0.82rem" }}>
                          Days to Complete: <strong>{days_to_complete ?? "N/A"}</strong>
                        </Typography>
                        {notes && (
                          <Typography variant="body2" sx={{ mt: 0.45, fontStyle: "italic", color: "#1565C0", fontSize: "0.8rem", wordBreak: "break-word", overflowWrap: "anywhere" }}>
                            Notes: "{notes}"
                          </Typography>
                        )}
                      </Box>
                    )}

                    {/* ── Comment body ── */}
                    {type === "comment" && (
                      <Box sx={{ p: 1, borderRadius: "10px", backgroundColor: "#f9fafb", border: "1px solid rgba(47,99,173,0.06)", mb: 0.75 }}>
                        <Typography variant="body2" sx={{ color: "#555", fontStyle: "italic", fontSize: "0.82rem", wordBreak: "break-word", overflowWrap: "anywhere" }}>
                          "{title}"
                        </Typography>
                      </Box>
                    )}

                    {/* ── Body text for non-special types ── */}
                    {type !== "status" && type !== "dates" && type !== "completion_days" && type !== "comment" && (
                      <Typography variant="body2" sx={{ color: "#223142", mb: by || time ? 0.55 : 0, fontSize: "0.82rem", wordBreak: "break-word", overflowWrap: "anywhere" }}>
                        {title}
                      </Typography>
                    )}

                    {/* ── By line ── */}
                    {by && (
                      <Typography variant="body2" sx={{ color: "#6b778c", mb: 0.35, fontSize: "0.77rem", wordBreak: "break-word", overflowWrap: "anywhere" }}>
                        {type === "architecturalRequest"
                          ? "Created by "
                          : type === "email"
                          ? "To "
                          : type === "status"
                          ? "Changed by "
                          : "By "}
                        <strong>{by}</strong>
                        {email && (
                          <Typography component="span" variant="caption" sx={{ ml: 0.5, color: "#8a94a6", fontSize: "0.72rem", wordBreak: "break-all" }}>
                            ({email})
                          </Typography>
                        )}
                      </Typography>
                    )}

                    {/* ── Timestamp ── */}
                    <Typography variant="caption" sx={{ fontSize: "0.72rem", color: "#8a94a6", fontStyle: "italic", display: "flex", alignItems: "center", mt: 0.25, wordBreak: "break-word" }}>
                      <AccessTimeIcon sx={{ fontSize: 11, mr: 0.45 }} />
                      {new Date(time).toLocaleString()}
                    </Typography>
                  </Box>
                </Card>
              </Box>
            </motion.div>
          );
        })}
      </Box>

      <Legend />
    </Box>
  );
};

const Legend = () => {
  const items = ["architecturalRequest", "status", "dates", "completion_days", "comment", "event", "email"];

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="body2" sx={{ fontWeight: 700, color: "#223142", mb: 0.75 }}>
        Legend
      </Typography>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
        {items.map((key) => {
          const item          = timelineTypeConfig[key];
          const IconComponent = item.icon;
          return (
            <Chip
              key={key}
              icon={<IconComponent sx={{ fontSize: "14px !important" }} />}
              label={item.label}
              size="small"
              sx={{
                borderRadius: "999px",
                fontWeight: 600,
                fontSize: "0.72rem",
                color: item.color,
                backgroundColor: `${item.color}15`,
              }}
            />
          );
        })}
      </Box>
    </Box>
  );
};

export default ArchitecturalRequestTimeline;
